// Danh mục toàn bộ chủ đề Bói Đê — trang công khai, khách xem được để chọn
// trước rồi mới đăng nhập. Bấm vào một chủ đề thì mở luồng xem ở /tong-hop/$topic.
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarHeart, ChevronRight, Compass, Search, Users, X } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  formatVnd,
  topicAccent,
  topicDisplayName,
  topicIcon,
  GROUP_META,
} from "@/components/boi-de/topic-style";
import { SiteFooter } from "@/components/site-footer";
import { listBoiDeTopics, type BoiDeTopic } from "@/lib/boide-api";

const OTHER_GROUP = { key: "khac", label: "Chủ đề khác" };

export const Route = createFileRoute("/chu-de")({
  head: () => ({
    meta: [
      { title: "Tất cả chủ đề bói - Tình duyên, Sự nghiệp, Tài lộc | Bói Đê" },
      {
        name: "description",
        content:
          "Xem toàn bộ chủ đề của Bói Đê: tình duyên, hợp tác làm ăn, tài lộc, sự nghiệp, gia đạo, sức khỏe. Chọn chủ đề bạn quan tâm và nhận luận giải từ AI.",
      },
      { property: "og:title", content: "Tất cả chủ đề bói | Bói Đê" },
      {
        property: "og:description",
        content: "Chọn chủ đề bạn đang băn khoăn — tình duyên, công danh, tài lộc, gia đạo.",
      },
      { property: "og:url", content: "https://boide.online/chu-de" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/chu-de" }],
  }),
  component: ChuDePage,
});

function groupLabel(key: string): string {
  return GROUP_META.find((g) => g.key === key)?.label ?? OTHER_GROUP.label;
}

function ChuDePage() {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<string>("tat-ca");

  const topics = useQuery({
    queryKey: ["boi-de-topics"],
    queryFn: listBoiDeTopics,
    staleTime: 30 * 60 * 1000,
  });

  // Ẩn chủ đề "tự do" (Bói tổng hợp) — luồng này đã gộp vào /tong-hop.
  const all = useMemo(
    () => (topics.data ?? []).filter((topic) => topic.input_kind !== "free"),
    [topics.data],
  );

  // Chỉ hiện nhóm thực sự có chủ đề, giữ đúng thứ tự GROUP_META.
  const groups = useMemo(() => {
    const keys = new Set(all.map((t) => t.group_key || OTHER_GROUP.key));
    const ordered = GROUP_META.filter((g) => keys.has(g.key)).map((g) => g.key);
    return keys.has(OTHER_GROUP.key) ? [...ordered, OTHER_GROUP.key] : ordered;
  }, [all]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((t) => {
      const inGroup = group === "tat-ca" || (t.group_key || OTHER_GROUP.key) === group;
      const displayName = topicDisplayName(t.slug, t.name);
      const inQuery = !q || `${displayName} ${t.name} ${t.description}`.toLowerCase().includes(q);
      return inGroup && inQuery;
    });
  }, [all, group, query]);

  const sections = useMemo(() => {
    return groups
      .map((key) => ({
        key,
        label: groupLabel(key),
        items: filtered.filter((t) => (t.group_key || OTHER_GROUP.key) === key),
      }))
      .filter((s) => s.items.length > 0);
  }, [groups, filtered]);

  return (
    <main className="min-h-0 flex-1 overflow-y-auto pb-24 md:pb-14">
      <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 md:py-9 lg:px-8">
        <header className="max-w-2xl">
          <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Câu hỏi gợi ý
          </h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Chọn đúng điều bạn đang băn khoăn. Mỗi chủ đề có cách hỏi và khung luận riêng, giá hiển
            thị ngay trên thẻ.
          </p>
        </header>

        {/* Vận khí không phải "chủ đề trả phí" nên không nằm trong bảng boi_de_topics —
            đặt thành thẻ nổi riêng ở đầu danh mục. */}
        <Link
          to="/tong-hop/van-khi"
          className="group mt-5 flex items-center gap-4 rounded-2xl border border-gold-soft/40 bg-card/70 px-4 py-4 transition hover:border-primary/50"
        >
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary">
            <CalendarHeart className="size-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-display text-base font-semibold text-foreground">Vận khí</span>
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                MIỄN PHÍ
              </span>
            </span>
            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
              Vận ngày · vận tuần · vận tháng âm lịch · nhãn ngày tốt. Mỗi kỳ mở đúng hẹn rồi đóng
              sau 24 giờ.
            </span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground transition group-hover:text-primary" />
        </Link>

        <Link
          to="/tong-hop/la-ban"
          className="group mt-3 flex items-center gap-4 rounded-2xl border border-gold-soft/40 bg-card/70 px-4 py-4 transition hover:border-primary/50"
        >
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary">
            <Compass className="size-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-display text-base font-semibold text-foreground">
                La bàn phong thuỷ
              </span>
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                MIỄN PHÍ
              </span>
            </span>
            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
              La bàn Bát Trạch theo cung phi của bạn, và la bàn hướng theo khí từng ngày — xoay theo
              cảm biến điện thoại.
            </span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground transition group-hover:text-primary" />
        </Link>

        <label className="mt-5 flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2.5 focus-within:border-primary/50">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Tìm chủ đề — tình duyên, tài lộc, sự nghiệp…"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Xoá tìm kiếm"
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </label>

        {groups.length > 1 ? (
          <div className="scrollbar-none -mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
            <GroupChip active={group === "tat-ca"} onClick={() => setGroup("tat-ca")}>
              Tất cả
            </GroupChip>
            {groups.map((key) => (
              <GroupChip key={key} active={group === key} onClick={() => setGroup(key)}>
                {groupLabel(key)}
              </GroupChip>
            ))}
          </div>
        ) : null}

        {topics.isPending ? (
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-[132px] rounded-2xl" />
            ))}
          </div>
        ) : topics.isError ? (
          <p className="mt-8 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            Chưa tải được danh sách chủ đề. Vui lòng thử lại sau.
          </p>
        ) : sections.length === 0 ? (
          <p className="mt-8 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            Không tìm thấy chủ đề nào khớp “{query}”. Thử từ khoá khác xem sao.
          </p>
        ) : (
          <div className="mt-7 space-y-9">
            {sections.map((section) => (
              <section key={section.key} aria-labelledby={`nhom-${section.key}`}>
                <h2
                  id={`nhom-${section.key}`}
                  className="font-display text-base font-bold text-foreground sm:text-lg"
                >
                  {section.label}
                  <span className="ml-2 text-xs font-medium text-muted-foreground">
                    {section.items.length} chủ đề
                  </span>
                </h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {section.items.map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <SiteFooter />
      </div>
    </main>
  );
}

function GroupChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground hover:border-primary/45 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function TopicCard({ topic }: { topic: BoiDeTopic }) {
  const Icon = topicIcon(topic.icon);
  const accent = topicAccent(topic.slug);
  const nhieuNguoi = topic.max_persons > topic.min_persons;

  return (
    <Link
      to="/tong-hop/$topic"
      params={{ topic: topic.slug }}
      className={cn(
        "group flex flex-col rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:shadow-md",
        accent.border,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-2xl",
            accent.bg,
            accent.text,
          )}
        >
          <Icon className="topic-icon--light size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[15px] font-bold leading-snug text-foreground">
            {topicDisplayName(topic.slug, topic.name)}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
            {topic.description}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold tabular-nums text-primary">{formatVnd(topic.price)}</p>
          {nhieuNguoi ? (
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Users className="size-3" />
              {topic.min_persons}–{topic.max_persons} người
              {topic.extra_person_price > 0
                ? ` · thêm ${formatVnd(topic.extra_person_price)}/người`
                : ""}
            </p>
          ) : null}
        </div>
        <span className="inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold text-primary">
          Xem
          <ChevronRight className="size-3.5 transition group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}
